import React, { useEffect, useState, useRef } from 'react';
import { FaTimes, FaDownload, FaSpinner, FaChevronLeft, FaChevronRight, FaPen, FaTrash, FaUserPlus } from 'react-icons/fa';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db, storage } from '../config/firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { PDFDocument, rgb } from 'pdf-lib';
import { SignPdf } from '@signpdf/signpdf';
import { P12Signer } from '@signpdf/signer-p12';
import { plainAddPlaceholder } from '@signpdf/placeholder-plain';
import { Buffer } from 'buffer';

// Polyfill Buffer para el navegador
if (typeof window !== 'undefined') {
  (window as any).Buffer = Buffer;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onPdfSigned?: () => void;
  task: any;
  client?: any;
  currentUser?: any;
  userData?: any;
  language?: 'es' | 'en';
}

const ScanningReportModal: React.FC<Props> = ({ isOpen, onClose, onPdfSigned, task, client, currentUser, userData }) => {
  const [pages, setPages] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [signature, setSignature] = useState<string>('');
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasCertificate, setHasCertificate] = useState(false);
  const [certificateData, setCertificateData] = useState<any>(null);
  const [certificatePassword, setCertificatePassword] = useState('');
  const [signerName, setSignerName] = useState('');
  const [signDate] = useState(new Date().toISOString().split('T')[0]);
  const [isSigning, setIsSigning] = useState(false);
  const [signedPdfUrl, setSignedPdfUrl] = useState<string>('');
  const [signedPdfData, setSignedPdfData] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [saveSignature, setSaveSignature] = useState(false);
  const [isSavingSignature, setIsSavingSignature] = useState(false);
  
  // Estados para firmantes
  const [availableSigners, setAvailableSigners] = useState<any[]>([]);
  const [preparedBy, setPreparedBy] = useState<string>('');
  const [supervisedBy, setSupervisedBy] = useState<string>('');
  const [preparedBySignature, setPreparedBySignature] = useState<string>('');
  const [supervisedBySignature, setSupervisedBySignature] = useState<string>('');
  const [preparedByCertPassword, setPreparedByCertPassword] = useState('');
  const [supervisedByCertPassword, setSupervisedByCertPassword] = useState('');
  const [preparedByData, setPreparedByData] = useState<any>(null);
  const [supervisedByData, setSupervisedByData] = useState<any>(null);
  const [useVisualSignatureOnly, setUseVisualSignatureOnly] = useState(false);
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const modalCanvasRef = useRef<HTMLCanvasElement>(null);
  const preparedByCanvasRef = useRef<HTMLCanvasElement>(null);
  const supervisedByCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const fetchBusinessLogoAndGenerate = async () => {
      let logoUrl = '';
      // Usar task.businessId primero, luego userData.businessId como fallback
      const businessId = task?.businessId || userData?.businessId;
      if (businessId) {
        try {
          const businessDoc = await getDoc(doc(db, 'businesses', businessId));
          if (businessDoc.exists()) {
            const businessData = businessDoc.data();
            logoUrl = businessData?.logo || '';
            console.log('[ScanningReportModal] Business logo URL:', logoUrl);
          }
        } catch (error) {
          console.error('Error fetching business logo:', error);
        }
      }
      // Generar preview después de obtener el logo, pasándolo como parámetro
      await generatePreview(logoUrl);
    };

    const fetchUserSignatureData = async () => {
      if (currentUser?.uid) {
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userInfo = userDoc.data();
            console.log('[SignatureLoad] User data:', userInfo);
            
            // Verificar si tiene certificado de firma digital
            const digitalSig = userInfo?.digitalSignature;
            const hasCert = digitalSig?.certificateUrl && 
                           digitalSig?.status === 'active' ? true : false;
            setHasCertificate(hasCert);
            
            // Guardar datos del certificado
            if (hasCert) {
              setCertificateData({
                certificateName: digitalSig.certificateName,
                certificateUrl: digitalSig.certificateUrl,
                issuerName: digitalSig.issuerName,
                organization: digitalSig.organization,
                country: digitalSig.country,
                expiryDate: digitalSig.expiryDate,
                serialNumber: digitalSig.serialNumber
              });
            }
            
            // Cargar firma guardada si existe
            if (userInfo?.savedSignature?.url) {
              console.log('[SignatureLoad] Found saved signature:', userInfo.savedSignature.url);
              const img = new Image();
              img.crossOrigin = 'anonymous';
              img.onload = () => {
                console.log('[SignatureLoad] Image loaded successfully');
                const canvas = canvasRef.current;
                if (!canvas) {
                  console.log('[SignatureLoad] Canvas not available');
                  return;
                }
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                  console.log('[SignatureLoad] Context not available');
                  return;
                }
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                const dataUrl = canvas.toDataURL();
                setSignature(dataUrl);
                console.log('[SignatureLoad] Signature loaded and set');
              };
              img.onerror = (err) => {
                console.error('[SignatureLoad] Error loading image:', err);
              };
              img.src = userInfo.savedSignature.url;
            } else {
              console.log('[SignatureLoad] No saved signature found');
            }
            
            // Establecer nombre del firmante
            const fullName = userInfo?.firstName && userInfo?.lastName 
              ? `${userInfo.firstName} ${userInfo.lastName}`
              : userInfo?.name || currentUser.displayName || currentUser.email || '';
            setSignerName(fullName);
          }
        } catch (error) {
          console.error('Error fetching user signature data:', error);
        }
      }
    };

    const fetchAvailableSigners = async () => {
      if (!task?.projectId || !task?.businessId || !currentUser?.uid) return;
      
      try {
        // Obtener el proyecto para ver el staff
        const projectDoc = await getDoc(doc(db, 'projects', task.businessId, 'projects', task.projectId));
        if (!projectDoc.exists()) return;
        
        const projectData = projectDoc.data();
        const staff = projectData.staff || [];
        
        // Encontrar el usuario actual en el staff
        const currentUserInStaff = staff.find((m: any) => (m.id || m.uid) === currentUser.uid);
        const currentUserPermissions = currentUserInStaff?.canSignForMe || {};
        
        // Crear lista de firmantes disponibles: usuario actual + quienes le permiten firmar
        const signers: any[] = [];
        
        // Agregar usuario actual
        const currentUserDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (currentUserDoc.exists()) {
          const currentUserData = currentUserDoc.data();
          // Buscar el projectRole en el staff
          const currentUserStaffData = staff.find((m: any) => (m.id || m.uid) === currentUser.uid);
          const projectRole = currentUserStaffData?.projectRole || '';
          
          signers.push({
            uid: currentUser.uid,
            id: currentUser.uid,
            name: `${currentUserData.firstName || ''} ${currentUserData.lastName || ''}`.trim() || currentUserData.email,
            email: currentUserData.email,
            projectRole: projectRole,
            isSelf: true,
            savedSignature: currentUserData.savedSignature,
            digitalSignature: currentUserData.digitalSignature
          });
        }
        
        // Agregar usuarios que permiten que el actual firme por ellos
        for (const [userId, hasPermission] of Object.entries(currentUserPermissions)) {
          if (hasPermission) {
            const userDoc = await getDoc(doc(db, 'users', userId));
            if (userDoc.exists()) {
              const userDocData = userDoc.data();
              // Buscar el projectRole en el staff
              const userStaffData = staff.find((m: any) => (m.id || m.uid) === userId);
              const projectRole = userStaffData?.projectRole || '';
              
              signers.push({
                uid: userId,
                id: userId,
                name: `${userDocData.firstName || ''} ${userDocData.lastName || ''}`.trim() || userDocData.email,
                email: userDocData.email,
                projectRole: projectRole,
                isSelf: false,
                savedSignature: userDocData.savedSignature,
                digitalSignature: userDocData.digitalSignature
              });
            }
          }
        }
        
        setAvailableSigners(signers);
        
        // Establecer prepared by por defecto al usuario actual
        if (signers.length > 0) {
          setPreparedBy(currentUser.uid);
        }
      } catch (error) {
        console.error('Error fetching available signers:', error);
      }
    };

    if (isOpen && task) {
      // Verificar si el task ya tiene un PDF firmado
      const existingSignedPdf = task.scanningReport?.signedPdf;
      if (existingSignedPdf) {
        setSignedPdfUrl(existingSignedPdf.url);
        setSignedPdfData(existingSignedPdf);
        // Si ya hay PDF firmado, generar preview desde ese PDF
        generatePreviewFromSignedPdf(existingSignedPdf.url);
      } else {
        fetchBusinessLogoAndGenerate();
      }
      fetchUserSignatureData();
      fetchAvailableSigners();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, task, userData?.businessId, currentUser?.uid]);

  // Cargar datos del firmante "Prepared By"
  useEffect(() => {
    const loadPreparedByData = async () => {
      if (!preparedBy) {
        setPreparedByData(null);
        setPreparedBySignature('');
        return;
      }
      
      const signer = availableSigners.find(s => s.uid === preparedBy);
      if (!signer) return;
      
      setPreparedByData(signer);
      
      // Cargar firma guardada si existe
      if (signer.savedSignature?.url) {
        // Establecer la firma inmediatamente para habilitar el botón
        setPreparedBySignature(signer.savedSignature.url);
        
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const canvas = preparedByCanvasRef.current;
          if (!canvas) return;
          const ctx = canvas.getContext('2d');
          if (!ctx) return;
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          // Actualizar con el dataURL del canvas para uso en el PDF
          setPreparedBySignature(canvas.toDataURL());
        };
        img.onerror = () => {
          console.error('Error loading Prepared By signature image');
        };
        img.src = signer.savedSignature.url;
      } else {
        setPreparedBySignature('');
      }
    };
    
    loadPreparedByData();
  }, [preparedBy, availableSigners]);

  // Cargar datos del firmante "Supervised By"
  useEffect(() => {
    const loadSupervisedByData = async () => {
      if (!supervisedBy) {
        setSupervisedByData(null);
        setSupervisedBySignature('');
        return;
      }
      
      const signer = availableSigners.find(s => s.uid === supervisedBy);
      if (!signer) return;
      
      setSupervisedByData(signer);
      
      // Cargar firma guardada si existe
      if (signer.savedSignature?.url) {
        // Establecer la firma inmediatamente
        setSupervisedBySignature(signer.savedSignature.url);
        
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const canvas = supervisedByCanvasRef.current;
          if (!canvas) return;
          const ctx = canvas.getContext('2d');
          if (!ctx) return;
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          // Actualizar con el dataURL del canvas
          setSupervisedBySignature(canvas.toDataURL());
        };
        img.onerror = () => {
          console.error('Error loading Supervised By signature image');
        };
        img.src = signer.savedSignature.url;
      } else {
        setSupervisedBySignature('');
      }
    };
    
    loadSupervisedByData();
  }, [supervisedBy, availableSigners]);

  // Regenerar PDF cuando cambien los firmantes
  useEffect(() => {
    const regeneratePDF = async () => {
      // Solo regenerar si hay firmantes seleccionados y no hay PDF firmado
      if (!signedPdfUrl && isOpen && task && (preparedByData || supervisedByData)) {
        console.log('[PDF Regeneration] Signers changed, regenerating preview...');
        const businessId = task?.businessId || userData?.businessId;
        if (businessId) {
          try {
            const businessDoc = await getDoc(doc(db, 'businesses', businessId));
            if (businessDoc.exists()) {
              const businessData = businessDoc.data();
              const logoUrl = businessData?.logo || '';
              await generatePreview(logoUrl);
            }
          } catch (error) {
            console.error('Error regenerating preview:', error);
          }
        }
      }
    };
    
    regeneratePDF();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preparedByData, supervisedByData]);

  const formatDate = (d: Date) => {
    return d.toLocaleDateString();
  };

  const handleOpenSignatureModal = async () => {
    setShowSignatureModal(true);
    
    // Recargar datos del usuario para obtener la firma guardada más reciente
    if (currentUser?.uid) {
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userInfo = userDoc.data();
          console.log('[ModalOpen] User savedSignature:', userInfo?.savedSignature);
          
          // Cargar firma guardada si existe
          if (userInfo?.savedSignature?.url) {
            console.log('[ModalOpen] Loading saved signature from:', userInfo.savedSignature.url);
            setTimeout(() => {
              const img = new Image();
              img.crossOrigin = 'anonymous';
              img.onload = () => {
                console.log('[ModalOpen] Image loaded successfully');
                const canvas = modalCanvasRef.current;
                if (!canvas) {
                  console.log('[ModalOpen] Modal canvas not available');
                  return;
                }
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                  console.log('[ModalOpen] Modal context not available');
                  return;
                }
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                console.log('[ModalOpen] Signature drawn on modal canvas');
              };
              img.onerror = (err) => {
                console.error('[ModalOpen] Error loading saved signature:', err);
              };
              img.src = userInfo.savedSignature.url;
            }, 100); // Pequeño delay para asegurar que el canvas está montado
          } else {
            console.log('[ModalOpen] No saved signature found in user data');
          }
        }
      } catch (error) {
        console.error('[ModalOpen] Error loading user data:', error);
      }
    }
  };

  const handleSaveSignatureFromModal = async () => {
    const canvas = modalCanvasRef.current;
    if (!canvas) return;
    
    const signatureDataUrl = canvas.toDataURL();
    setSignature(signatureDataUrl);
    
    // Copiar al canvas pequeño
    const smallCanvas = canvasRef.current;
    if (smallCanvas) {
      const ctx = smallCanvas.getContext('2d');
      if (ctx) {
        const img = new Image();
        img.onload = () => {
          ctx.clearRect(0, 0, smallCanvas.width, smallCanvas.height);
          ctx.drawImage(img, 0, 0, smallCanvas.width, smallCanvas.height);
        };
        img.src = signatureDataUrl;
      }
    }
    
    // Guardar en Firebase si el usuario lo solicitó
    if (saveSignature && currentUser?.uid) {
      setIsSavingSignature(true);
      console.log('[SaveSignature] Starting save process for user:', currentUser.uid);
      try {
        // Convertir dataURL a Blob
        const response = await fetch(signatureDataUrl);
        const blob = await response.blob();
        console.log('[SaveSignature] Blob created, size:', blob.size);
        
        // Subir a Storage
        const timestamp = Date.now();
        const storagePath = `users/${currentUser.uid}/signature/signature_${timestamp}.png`;
        const storageRef = ref(storage, storagePath);
        console.log('[SaveSignature] Uploading to:', storagePath);
        await uploadBytes(storageRef, blob);
        const downloadURL = await getDownloadURL(storageRef);
        console.log('[SaveSignature] Upload complete, URL:', downloadURL);
        
        // Guardar URL en el documento del usuario
        const userRef = doc(db, 'users', currentUser.uid);
        const signatureData = {
          url: downloadURL,
          storagePath: storagePath,
          uploadedAt: new Date().toISOString()
        };
        console.log('[SaveSignature] Updating Firestore with:', signatureData);
        await updateDoc(userRef, {
          savedSignature: signatureData
        });
        
        console.log('✅ Signature saved successfully to Firestore');
      } catch (error) {
        console.error('❌ Error saving signature:', error);
        alert('Error al guardar la firma. Por favor intenta de nuevo.');
      } finally {
        setIsSavingSignature(false);
      }
    } else {
      console.log('[SaveSignature] Not saving - saveSignature:', saveSignature, 'uid:', currentUser?.uid);
    }
    
    setShowSignatureModal(false);
    setSaveSignature(false);
  };

  const getPreparedByBlock = () => {
    // Usar datos del firmante seleccionado "Prepared By" solamente
    let name = '';
    let email = '';
    let projectRole = '';
    
    if (preparedBy && preparedByData) {
      name = preparedByData.name;
      email = preparedByData.email;
      projectRole = preparedByData.projectRole || '';
    } else {
      name = (userData && ((userData.firstName && userData.lastName) ? `${userData.firstName} ${userData.lastName}` : (userData.name || ''))) || (currentUser && (currentUser.displayName || currentUser.email)) || '';
      email = (userData && (userData.email || '')) || (currentUser && currentUser.email) || '';
    }

    return `
      <div style="margin-top:20px;">
        <div style="font-weight:600; margin-bottom:6px;">Prepared By:</div>
        <div style="font-size:14px;">${name}</div>
        ${projectRole ? `<div style="font-size:14px; color:#6b7280;">${projectRole}</div>` : ''}
        <div style="font-size:14px;">${email}</div>
      </div>
    `;
  };

  const generatePreview = async (logoUrl = '') => {
    if (!task) return;
    try {
      setIsGenerating(true);
      setPages([]);
      setCurrentPage(0);

      const clientName = client?.name || client?.businessName || task.clientName || '';
      
      // Extraer datos de location del task
      const taskLocation = task.location || {};
      const siteAddress = taskLocation.address || '';
      const siteCity = taskLocation.city || '';
      const siteState = taskLocation.state || '';
      const siteZipCode = taskLocation.zipCode || '';
      const siteCountry = taskLocation.country || '';
      
      // Construir string de ubicación completo
      const location = siteAddress || [siteCity, siteState, siteZipCode, siteCountry].filter(Boolean).join(', ');

      const now = new Date();

      // Primera página HTML (centrada en A4) con pie centrado
      const firstPageHTML = `
        <div style="width:100%; height:1123px; position:relative; background:white; box-sizing:border-box; padding-top:140px; padding-bottom:150px; display:flex; align-items:center; justify-content:center;">
          ${logoUrl ? `<div style="position:absolute; top:20px; right:20px; width:150px; height:100px; display:flex; align-items:center; justify-content:center; z-index:10;">
            <img src="${logoUrl}" alt="Business Logo" crossorigin="anonymous" style="max-width:100%; max-height:100%; width:auto; height:auto; object-fit:contain;"/>
          </div>` : ''}
          
          <div style="font-family: Arial, Helvetica, sans-serif; color:#111; max-width:700px; width:100%; padding:0 40px; text-align:center;">
            <h1 style="font-size:28px; color:#1f2937; margin:0 0 10px 0; font-weight:bold; text-transform:uppercase;">Summary of Scanning</h1>
            <div style="margin-top:18px; font-size:16px;">
              <div><strong>Prepared For:</strong> ${clientName}</div>
              <div style="margin-top:6px;"><strong>Date:</strong> ${formatDate(now)}</div>
            </div>
            ${getPreparedByBlock()}
          </div>

          <div style="position:absolute; bottom:20px; left:0; right:0; display:flex; flex-direction:column; justify-content:center; align-items:center; gap:8px;">
            <span style="font-size:12px; color:#6b7280; font-weight:500;">Created on</span>
            <img src="https://firebasestorage.googleapis.com/v0/b/brix-v2.firebasestorage.app/o/Marca%2FRecurso%202.png?alt=media&token=4f421958-d11b-4476-9cbd-0826ac21ebba" alt="Brix" style="width:150px; height:auto; object-fit:contain;"/>
          </div>
        </div>
      `;

      // Segunda página HTML (sin restricción de altura para permitir múltiples páginas)
      const scanning = task.scanningData || {};
      const imageSets = (scanning.images || []);
      
      const purpose = scanning.purpose || '';
      const equipment = scanning.equipment || '';
      const process = scanning.process || '';
      const limitations = scanning.limitations || '';
      const findings = scanning.findings || 'No findings were recorded for this section.';
      const closure = scanning.closure || '';

      const brixLogoPng = 'https://firebasestorage.googleapis.com/v0/b/brix-v2.firebasestorage.app/o/Marca%2FRecurso%202.png?alt=media&token=4f421958-d11b-4476-9cbd-0826ac21ebba';
      
      console.log('[ScanningReport] Datos para segunda página:', {
        purpose: purpose?.substring(0, 50),
        equipment: equipment?.substring(0, 50),
        process: process?.substring(0, 50),
        limitations: limitations?.substring(0, 50),
        findings: findings?.substring(0, 50),
        clientName,
        location
      });
      


      // Tercera página: tabla de imágenes (escaneos vs diagramas) - se creará dinámicamente más adelante

      // Crear contenedores temporales y capturar con html2canvas
      const A4_WIDTH_PX = 794; // ~210mm at 96dpi
      const A4_HEIGHT_PX = 1123; // ~297mm at 96dpi
      
      // Primera página - tamaño fijo
      const firstPageContainer = document.createElement('div');
      firstPageContainer.style.position = 'absolute';
      firstPageContainer.style.left = '-9999px';
      firstPageContainer.style.top = '0';
      firstPageContainer.style.width = `${A4_WIDTH_PX}px`;
      firstPageContainer.style.height = `${A4_HEIGHT_PX}px`;
      firstPageContainer.style.backgroundColor = 'white';
      firstPageContainer.style.padding = '0';
      firstPageContainer.style.boxSizing = 'border-box';
      firstPageContainer.innerHTML = firstPageHTML;
      document.body.appendChild(firstPageContainer);

      // Segunda página - crear con DOM en lugar de innerHTML
      const secondPageContainer = document.createElement('div');
      secondPageContainer.style.position = 'absolute';
      secondPageContainer.style.left = '-9999px';
      secondPageContainer.style.top = '0';
      secondPageContainer.style.width = `${A4_WIDTH_PX}px`;
      secondPageContainer.style.backgroundColor = 'white';
      secondPageContainer.style.padding = '60px';
      secondPageContainer.style.boxSizing = 'border-box';
      secondPageContainer.style.fontFamily = 'Arial, Helvetica, sans-serif';
      secondPageContainer.style.color = '#111';
      
      // Contenedor de cliente
      const clientBox = document.createElement('div');
      clientBox.style.background = '#f8fafc';
      clientBox.style.borderLeft = '4px solid #3b82f6';
      clientBox.style.padding = '24px';
      clientBox.style.marginTop = '0';
      clientBox.style.borderRadius = '8px';
      
      const companyLabel = document.createElement('div');
      companyLabel.style.fontSize = '14px';
      companyLabel.style.color = '#64748b';
      companyLabel.style.marginBottom = '8px';
      companyLabel.textContent = 'Company';
      
      const companyValue = document.createElement('div');
      companyValue.style.fontSize = '16px';
      companyValue.style.color = '#0f172a';
      companyValue.style.fontWeight = '600';
      companyValue.textContent = clientName;
      
      const siteContainer = document.createElement('div');
      siteContainer.style.marginTop = '16px';
      
      const siteLabel = document.createElement('div');
      siteLabel.style.fontSize = '14px';
      siteLabel.style.color = '#64748b';
      siteLabel.style.marginBottom = '8px';
      siteLabel.textContent = 'Site';
      
      const siteValue = document.createElement('div');
      siteValue.style.fontSize = '16px';
      siteValue.style.color = '#0f172a';
      siteValue.style.fontWeight = '600';
      siteValue.textContent = location;
      
      siteContainer.appendChild(siteLabel);
      siteContainer.appendChild(siteValue);
      clientBox.appendChild(companyLabel);
      clientBox.appendChild(companyValue);
      clientBox.appendChild(siteContainer);
      secondPageContainer.appendChild(clientBox);
      
      // Purpose
      if (purpose) {
        const purposeDiv = document.createElement('div');
        purposeDiv.style.marginTop = '32px';
        
        const purposeTitle = document.createElement('h3');
        purposeTitle.style.margin = '0 0 12px 0';
        purposeTitle.style.fontSize = '18px';
        purposeTitle.style.color = '#1e293b';
        purposeTitle.style.fontWeight = '600';
        purposeTitle.textContent = 'Purpose';
        
        const purposeText = document.createElement('p');
        purposeText.style.margin = '0';
        purposeText.style.fontSize = '14px';
        purposeText.style.color = '#334155';
        purposeText.style.lineHeight = '1.7';
        purposeText.style.textAlign = 'justify';
        purposeText.textContent = purpose;
        
        purposeDiv.appendChild(purposeTitle);
        purposeDiv.appendChild(purposeText);
        secondPageContainer.appendChild(purposeDiv);
      }
      
      // Equipment
      if (equipment) {
        const equipDiv = document.createElement('div');
        equipDiv.style.marginTop = '24px';
        
        const equipTitle = document.createElement('h3');
        equipTitle.style.margin = '0 0 12px 0';
        equipTitle.style.fontSize = '18px';
        equipTitle.style.color = '#1e293b';
        equipTitle.style.fontWeight = '600';
        equipTitle.textContent = 'Equipment';
        
        const equipText = document.createElement('p');
        equipText.style.margin = '0';
        equipText.style.fontSize = '14px';
        equipText.style.color = '#334155';
        equipText.style.lineHeight = '1.7';
        equipText.style.textAlign = 'justify';
        equipText.textContent = equipment;
        
        equipDiv.appendChild(equipTitle);
        equipDiv.appendChild(equipText);
        secondPageContainer.appendChild(equipDiv);
      }
      
      // Process
      if (process) {
        const processDiv = document.createElement('div');
        processDiv.style.marginTop = '24px';
        
        const processTitle = document.createElement('h3');
        processTitle.style.margin = '0 0 12px 0';
        processTitle.style.fontSize = '18px';
        processTitle.style.color = '#1e293b';
        processTitle.style.fontWeight = '600';
        processTitle.textContent = 'Process';
        
        const processText = document.createElement('p');
        processText.style.margin = '0';
        processText.style.fontSize = '14px';
        processText.style.color = '#334155';
        processText.style.lineHeight = '1.7';
        processText.style.textAlign = 'justify';
        processText.textContent = process;
        
        processDiv.appendChild(processTitle);
        processDiv.appendChild(processText);
        secondPageContainer.appendChild(processDiv);
      }
      
      // Limitations
      if (limitations) {
        const limDiv = document.createElement('div');
        limDiv.style.marginTop = '24px';
        
        const limTitle = document.createElement('h3');
        limTitle.style.margin = '0 0 12px 0';
        limTitle.style.fontSize = '18px';
        limTitle.style.color = '#1e293b';
        limTitle.style.fontWeight = '600';
        limTitle.textContent = 'Limitations';
        
        const limText = document.createElement('p');
        limText.style.margin = '0';
        limText.style.fontSize = '14px';
        limText.style.color = '#334155';
        limText.style.lineHeight = '1.7';
        limText.style.textAlign = 'justify';
        limText.textContent = limitations;
        
        limDiv.appendChild(limTitle);
        limDiv.appendChild(limText);
        secondPageContainer.appendChild(limDiv);
      }
      
      // Findings (si existe)
      if (findings && findings !== 'No findings were recorded for this section.') {
        const findingsDiv = document.createElement('div');
        findingsDiv.style.marginTop = '24px';
        
        const findingsTitle = document.createElement('h3');
        findingsTitle.style.margin = '0 0 12px 0';
        findingsTitle.style.fontSize = '18px';
        findingsTitle.style.color = '#1e293b';
        findingsTitle.style.fontWeight = '600';
        findingsTitle.textContent = 'Findings';
        
        const findingsText = document.createElement('p');
        findingsText.style.margin = '0';
        findingsText.style.fontSize = '14px';
        findingsText.style.color = '#334155';
        findingsText.style.lineHeight = '1.7';
        findingsText.style.textAlign = 'justify';
        findingsText.textContent = findings;
        
        findingsDiv.appendChild(findingsTitle);
        findingsDiv.appendChild(findingsText);
        secondPageContainer.appendChild(findingsDiv);
      }
      
      // Image Sets (Scan Sets) - agregar después de findings
      if (imageSets.length > 0) {
        const scanSetsDiv = document.createElement('div');
        scanSetsDiv.style.marginTop = '32px';
        
        const scanSetsTitle = document.createElement('h3');
        scanSetsTitle.style.margin = '0 0 16px 0';
        scanSetsTitle.style.fontSize = '18px';
        scanSetsTitle.style.color = '#1e293b';
        scanSetsTitle.style.fontWeight = '600';
        scanSetsTitle.textContent = 'Scan Sets';
        
        scanSetsDiv.appendChild(scanSetsTitle);
        
        // Crear tabla
        const table = document.createElement('table');
        table.style.width = '100%';
        table.style.borderCollapse = 'collapse';
        table.style.tableLayout = 'fixed';
        
        // Header de tabla
        const thead = document.createElement('thead');
        const headerRow = document.createElement('tr');
        
        const th1 = document.createElement('th');
        th1.style.width = '50%';
        th1.style.textAlign = 'left';
        th1.style.padding = '8px';
        th1.style.fontWeight = '600';
        th1.style.fontSize = '14px';
        th1.style.borderBottom = '2px solid #e5e7eb';
        th1.textContent = 'Scans';
        
        const th2 = document.createElement('th');
        th2.style.width = '50%';
        th2.style.textAlign = 'left';
        th2.style.padding = '8px';
        th2.style.fontWeight = '600';
        th2.style.fontSize = '14px';
        th2.style.borderBottom = '2px solid #e5e7eb';
        th2.textContent = 'Diagrams';
        
        headerRow.appendChild(th1);
        headerRow.appendChild(th2);
        thead.appendChild(headerRow);
        table.appendChild(thead);
        
        // Body de tabla
        const tbody = document.createElement('tbody');
        
        imageSets.forEach((set: any, index: number) => {
          const row = document.createElement('tr');
          if (index > 0) {
            row.style.borderTop = '1px solid #e5e7eb';
          }
          
          const td1 = document.createElement('td');
          td1.style.padding = '12px 8px';
          td1.style.verticalAlign = 'top';
          td1.style.textAlign = 'center';
          
          if (set.scanImage?.url) {
            const imgContainer = document.createElement('div');
            imgContainer.style.width = '100%';
            imgContainer.style.height = '240px'; // ~8cm a 96dpi
            imgContainer.style.display = 'flex';
            imgContainer.style.alignItems = 'center';
            imgContainer.style.justifyContent = 'center';
            
            const img1 = document.createElement('img');
            img1.src = set.scanImage.url;
            img1.crossOrigin = 'anonymous';
            img1.style.maxWidth = '330px'; // ~11cm a 96dpi
            img1.style.maxHeight = '240px'; // ~8cm a 96dpi
            img1.style.width = 'auto';
            img1.style.height = 'auto';
            img1.style.objectFit = 'contain';
            img1.style.border = '1px solid #e5e7eb';
            img1.style.borderRadius = '4px';
            imgContainer.appendChild(img1);
            td1.appendChild(imgContainer);
          } else {
            td1.style.color = '#9ca3af';
            td1.style.fontSize = '13px';
            td1.textContent = 'No scan';
          }
          
          const td2 = document.createElement('td');
          td2.style.padding = '12px 8px';
          td2.style.verticalAlign = 'top';
          td2.style.textAlign = 'center';
          
          if (set.diagramImage?.url) {
            const imgContainer = document.createElement('div');
            imgContainer.style.width = '100%';
            imgContainer.style.height = '240px'; // ~8cm a 96dpi
            imgContainer.style.display = 'flex';
            imgContainer.style.alignItems = 'center';
            imgContainer.style.justifyContent = 'center';
            
            const img2 = document.createElement('img');
            img2.src = set.diagramImage.url;
            img2.crossOrigin = 'anonymous';
            img2.style.maxWidth = '330px'; // ~11cm a 96dpi
            img2.style.maxHeight = '240px'; // ~8cm a 96dpi
            img2.style.width = 'auto';
            img2.style.height = 'auto';
            img2.style.objectFit = 'contain';
            img2.style.border = '1px solid #e5e7eb';
            img2.style.borderRadius = '4px';
            imgContainer.appendChild(img2);
            td2.appendChild(imgContainer);
          } else {
            td2.style.color = '#9ca3af';
            td2.style.fontSize = '13px';
            td2.textContent = 'No diagram';
          }
          
          row.appendChild(td1);
          row.appendChild(td2);
          tbody.appendChild(row);
        });
        
        table.appendChild(tbody);
        scanSetsDiv.appendChild(table);
        secondPageContainer.appendChild(scanSetsDiv);
      }
      
      // Closure (si existe)
      if (closure) {
        const closureText = document.createElement('p');
        closureText.style.margin = '32px 0 0 0';
        closureText.style.fontSize = '14px';
        closureText.style.color = '#334155';
        closureText.style.lineHeight = '1.7';
        closureText.style.textAlign = 'justify';
        closureText.textContent = closure;
        
        secondPageContainer.appendChild(closureText);
      }

      // Sección de firma digital (si existe signature)
      if (signature) {
        // Título "Electronically signed by:"
        const titleText = document.createElement('h3');
        titleText.style.margin = '20px 0 10px 0';
        titleText.style.fontSize = '16px';
        titleText.style.fontWeight = '400';
        titleText.style.color = '#1e293b';
        titleText.textContent = 'Electronically signed by:';
        secondPageContainer.appendChild(titleText);

        // Contenedor de firma con ancho fijo (un tercio del ancho total)
        const signatureContainer = document.createElement('div');
        signatureContainer.style.display = 'flex';
        signatureContainer.style.gap = '6px';
        signatureContainer.style.alignItems = 'center';
        signatureContainer.style.width = '250px';
        signatureContainer.style.marginTop = '10px';

        // Firma visual (izquierda - 70px fijo)
        const signatureColumn = document.createElement('div');
        signatureColumn.style.width = '70px';
        signatureColumn.style.height = '50px';
        signatureColumn.style.display = 'flex';
        signatureColumn.style.alignItems = 'center';
        signatureColumn.style.justifyContent = 'center';
        signatureColumn.style.flexShrink = '0';

        const signatureImg = document.createElement('img');
        signatureImg.src = signature;
        signatureImg.style.maxWidth = '70px';
        signatureImg.style.maxHeight = '50px';
        signatureImg.style.width = 'auto';
        signatureImg.style.height = 'auto';
        signatureImg.style.objectFit = 'contain';
        signatureImg.style.display = 'block';

        signatureColumn.appendChild(signatureImg);

        // Recuadro con información (derecha - 174px fijo)
        const textBoxContainer = document.createElement('div');
        textBoxContainer.style.width = '174px';
        textBoxContainer.style.minHeight = '50px';
        textBoxContainer.style.border = '1px solid #dc2626';
        textBoxContainer.style.borderRadius = '4px';
        textBoxContainer.style.padding = '6px 8px';
        textBoxContainer.style.position = 'relative';
        textBoxContainer.style.backgroundColor = '#ffffff';
        textBoxContainer.style.display = 'flex';
        textBoxContainer.style.flexDirection = 'column';
        textBoxContainer.style.justifyContent = 'center';
        textBoxContainer.style.flexShrink = '0';

        // Nombre del firmante en mayúsculas y negrita
        const signerNameText = document.createElement('p');
        signerNameText.style.margin = '0 0 3px 0';
        signerNameText.style.fontSize = '11px';
        signerNameText.style.fontWeight = '700';
        signerNameText.style.color = '#1e293b';
        signerNameText.style.textTransform = 'uppercase';
        signerNameText.style.letterSpacing = '0.3px';
        signerNameText.textContent = signerName;

        // Fecha y hora de firma
        const signDateText = document.createElement('p');
        signDateText.style.margin = '0';
        signDateText.style.fontSize = '8px';
        signDateText.style.color = '#64748b';
        signDateText.style.fontWeight = '400';
        const now = new Date();
        const formattedDate = now.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
        const formattedTime = now.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
        signDateText.textContent = `${formattedDate} ${formattedTime}`;

        textBoxContainer.appendChild(signerNameText);
        textBoxContainer.appendChild(signDateText);

        // Isotipo de Brix en esquina inferior derecha del recuadro
        const brixLogo = document.createElement('img');
        brixLogo.src = 'https://firebasestorage.googleapis.com/v0/b/brix-v2.firebasestorage.app/o/Marca%2FRecurso%201.svg?alt=media&token=6de5178f-af40-4927-82e7-404819dc6844';
        brixLogo.style.position = 'absolute';
        brixLogo.style.bottom = '3px';
        brixLogo.style.right = '3px';
        brixLogo.style.width = '14px';
        brixLogo.style.height = 'auto';
        brixLogo.style.opacity = '0.7';

        textBoxContainer.appendChild(brixLogo);

        signatureContainer.appendChild(signatureColumn);
        signatureContainer.appendChild(textBoxContainer);

        secondPageContainer.appendChild(signatureContainer);
      }
      
      document.body.appendChild(secondPageContainer);
      
      console.log('[ScanningReport] Contenedor segunda página:', {
        childElementCount: secondPageContainer.childElementCount,
        scrollHeight: secondPageContainer.scrollHeight
      });

      // Tercera página - tabla de imágenes (se creará después de calcular totalPages)

      // Esperar un poco para que las imágenes carguen
      await new Promise(resolve => setTimeout(resolve, 1200));

      const pageDataUrls: string[] = [];
      
      // Capturar primera página
      try {
        const canvas = await html2canvas(firstPageContainer, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
        pageDataUrls.push(canvas.toDataURL('image/png', 1.0));
      } catch (err) {
        console.error('html2canvas error for first page', err);
      }

      // Capturar segunda página usando técnica de división en páginas A4
      try {
        const fullContentHeight = secondPageContainer.scrollHeight;
        const HEADER_SPACE = 140; // Espacio reservado para el header (logo)
        const FOOTER_SPACE = 140; // Espacio reservado para el footer
        const EFFECTIVE_PAGE_HEIGHT = A4_HEIGHT_PX - HEADER_SPACE - FOOTER_SPACE; // Altura útil por página
        
        console.log('[ScanningReport] Contenido total altura:', fullContentHeight);
        console.log('[ScanningReport] A4 altura:', A4_HEIGHT_PX);
        console.log('[ScanningReport] Altura efectiva por página:', EFFECTIVE_PAGE_HEIGHT);
        const numSecondPages = Math.ceil(fullContentHeight / EFFECTIVE_PAGE_HEIGHT);
        console.log('[ScanningReport] Número de páginas para segunda sección:', numSecondPages);
        const totalPages = 1 + numSecondPages; // Solo portada + páginas de detalles (ya incluyen scan sets)
        
        // Crear múltiples contenedores A4, uno por página
        for (let i = 0; i < numSecondPages; i++) {
          console.log(`[ScanningReport] Capturando página ${i + 2} de ${totalPages}`);
          
          // Contenedor de página A4 con overflow hidden
          const pageWrapper = document.createElement('div');
          pageWrapper.style.position = 'absolute';
          pageWrapper.style.left = '-9999px';
          pageWrapper.style.top = '0';
          pageWrapper.style.width = `${A4_WIDTH_PX}px`;
          pageWrapper.style.height = `${A4_HEIGHT_PX}px`;
          pageWrapper.style.overflow = 'hidden';
          pageWrapper.style.backgroundColor = 'white';
          pageWrapper.style.boxSizing = 'border-box';
          
          // Agregar logo del negocio en cada página
          if (logoUrl) {
            const pageLogo = document.createElement('div');
            pageLogo.style.position = 'absolute';
            pageLogo.style.top = '30px';
            pageLogo.style.right = '30px';
            pageLogo.style.width = '120px';
            pageLogo.style.height = '80px';
            pageLogo.style.display = 'flex';
            pageLogo.style.alignItems = 'center';
            pageLogo.style.justifyContent = 'center';
            pageLogo.style.zIndex = '1000';
            
            const pageLogoImg = document.createElement('img');
            pageLogoImg.src = logoUrl;
            pageLogoImg.crossOrigin = 'anonymous';
            pageLogoImg.style.maxWidth = '100%';
            pageLogoImg.style.maxHeight = '100%';
            pageLogoImg.style.objectFit = 'contain';
            pageLogo.appendChild(pageLogoImg);
            pageWrapper.appendChild(pageLogo);
          }
          
          // Crear contenedor con padding fijo para contenido
          const contentWrapper = document.createElement('div');
          contentWrapper.style.position = 'absolute';
          contentWrapper.style.top = '140px';
          contentWrapper.style.left = '60px';
          contentWrapper.style.right = '60px';
          contentWrapper.style.height = `${EFFECTIVE_PAGE_HEIGHT}px`;
          contentWrapper.style.overflow = 'hidden';
          
          // Clonar todo el contenido completo
          const contentClone = secondPageContainer.cloneNode(true) as HTMLElement;
          contentClone.style.position = 'relative';
          contentClone.style.top = `-${i * EFFECTIVE_PAGE_HEIGHT}px`;
          contentClone.style.left = '-60px'; // Compensar el padding del contenedor original
          contentClone.style.margin = '0';
          
          contentWrapper.appendChild(contentClone);
          pageWrapper.appendChild(contentWrapper);
          
          // Agregar footer con paginación
          const footer = document.createElement('div');
          footer.style.position = 'absolute';
          footer.style.bottom = '20px';
          footer.style.left = '60px';
          footer.style.right = '60px';
          footer.style.textAlign = 'center';
          footer.style.fontSize = '10px';
          footer.style.color = '#888';
          footer.style.zIndex = '1000';
          
          const footerImg = document.createElement('img');
          footerImg.src = brixLogoPng;
          footerImg.alt = 'Brix';
          footerImg.crossOrigin = 'anonymous';
          footerImg.style.width = '50px';
          footerImg.style.height = '16px';
          footerImg.style.objectFit = 'contain';
          footerImg.style.verticalAlign = 'middle';
          footerImg.style.marginLeft = '4px';
          
          footer.appendChild(document.createTextNode(`Página ${2 + i} de ${totalPages} | Created on `));
          footer.appendChild(footerImg);
          
          pageWrapper.appendChild(footer);
          
          // Agregar al body temporalmente
          document.body.appendChild(pageWrapper);
          
          // Pequeña pausa para renderizado
          await new Promise(resolve => setTimeout(resolve, 150));
          
          // Capturar con html2canvas
          const canvas = await html2canvas(pageWrapper, { 
            scale: 2, 
            useCORS: true, 
            backgroundColor: '#ffffff',
            logging: false
          });
          pageDataUrls.push(canvas.toDataURL('image/png', 1.0));
          
          // Remover del DOM
          document.body.removeChild(pageWrapper);
        }
      } catch (err) {
        console.error('html2canvas error for second page', err);
      }

      // Remover contenedores
      document.body.removeChild(firstPageContainer);
      document.body.removeChild(secondPageContainer);

      setPages(pageDataUrls);
    } catch (error) {
      console.error('Error generating scanning preview', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = async () => {
    if (!pages || pages.length === 0) return;
    setIsGenerating(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210;
      const margin = 15;
      const imgWidth = pageWidth - margin * 2;

      for (let i = 0; i < pages.length; i++) {
        const img = pages[i];
        if (i > 0) pdf.addPage();
        // Crear imagen temporal para obtener dimensiones
        const image = new Image();
        image.src = img;
        await new Promise((resolve) => { image.onload = resolve; image.onerror = resolve; });
        const imgProps = { width: image.width, height: image.height };
        const imgHeight = (imgProps.height * imgWidth) / imgProps.width;
        pdf.addImage(img, 'PNG', margin, margin, imgWidth, imgHeight);
      }

      const filename = `scanning-report-draft-${task.id || Date.now()}.pdf`;
      pdf.save(filename);
    } catch (error) {
      console.error('Error exporting scanning PDF', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSignAndUpload = async () => {
    if (!pages || pages.length === 0) return;
    if (!preparedBy || !preparedBySignature) {
      alert('Please select a "Prepared By" signer with a valid signature');
      return;
    }
    
    console.log('[SignAndUpload] Iniciando proceso de firma...');
    console.log('[SignAndUpload] Prepared By:', preparedByData?.name);
    console.log('[SignAndUpload] Supervised By:', supervisedByData?.name || 'None');
    setIsSigning(true);
    
    try {
      // Paso 1: Generar PDF base con jsPDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 15;
      const imgWidth = pageWidth - margin * 2;

      // Agregar todas las páginas al PDF
      for (let i = 0; i < pages.length; i++) {
        const img = pages[i];
        if (i > 0) pdf.addPage();
        
        const image = new Image();
        image.src = img;
        await new Promise((resolve) => { image.onload = resolve; image.onerror = resolve; });
        const imgProps = { width: image.width, height: image.height };
        const imgHeight = (imgProps.height * imgWidth) / imgProps.width;
        pdf.addImage(img, 'PNG', margin, margin, imgWidth, imgHeight);
      }

      // Agregar sección de firmas digitales en la última página
      const now = new Date();
      const formattedDate = now.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
      const formattedTime = now.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
      
      // Posición inicial para firmas
      let signatureStartY = pageHeight - 75;
      
      // Título general
      pdf.setFontSize(8);
      pdf.setTextColor(30, 41, 59);
      pdf.setFont('helvetica', 'bold');
      pdf.text('ELECTRONIC SIGNATURES', margin, signatureStartY);
      
      signatureStartY += 5;
      
      // Firma 1: Prepared By
      if (preparedBySignature && preparedByData) {
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 41, 59);
        pdf.text('Prepared By:', margin, signatureStartY);
        
        signatureStartY += 2;
        
        // Firma visual
        const signatureWidth = (imgWidth * 0.20);
        const signatureHeight = 10;
        const signatureX = margin;
        const signatureY = signatureStartY;
        pdf.addImage(preparedBySignature, 'PNG', signatureX, signatureY, signatureWidth, signatureHeight);
        
        // Info box
        const boxX = signatureX + signatureWidth + 3;
        const boxWidth = imgWidth - signatureWidth - 3;
        const boxHeight = 12;
        const boxY = signatureY;
        
        pdf.setDrawColor(220, 38, 38);
        pdf.setLineWidth(0.3);
        pdf.roundedRect(boxX, boxY, boxWidth, boxHeight, 1, 1, 'S');
        
        pdf.setFontSize(6);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 41, 59);
        pdf.text(preparedByData.name.toUpperCase(), boxX + 2, boxY + 3);
        
        pdf.setFontSize(4.5);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 116, 139);
        if (preparedByData.projectRole) {
          pdf.text(preparedByData.projectRole, boxX + 2, boxY + 6);
        }
        pdf.text(preparedByData.email, boxX + 2, boxY + 9);
        pdf.text(`${formattedDate} ${formattedTime}`, boxX + 2, boxY + 11.5);
        
        signatureStartY += 15;
      }
      
      // Firma 2: Supervised By (opcional)
      if (supervisedBySignature && supervisedByData) {
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 41, 59);
        pdf.text('Supervised and Revised By:', margin, signatureStartY);
        
        signatureStartY += 2;
        
        // Firma visual
        const signatureWidth = (imgWidth * 0.20);
        const signatureHeight = 10;
        const signatureX = margin;
        const signatureY = signatureStartY;
        pdf.addImage(supervisedBySignature, 'PNG', signatureX, signatureY, signatureWidth, signatureHeight);
        
        // Info box
        const boxX = signatureX + signatureWidth + 3;
        const boxWidth = imgWidth - signatureWidth - 3;
        const boxHeight = 12;
        const boxY = signatureY;
        
        pdf.setDrawColor(59, 130, 246);
        pdf.setLineWidth(0.3);
        pdf.roundedRect(boxX, boxY, boxWidth, boxHeight, 1, 1, 'S');
        
        pdf.setFontSize(6);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 41, 59);
        pdf.text(supervisedByData.name.toUpperCase(), boxX + 2, boxY + 3);
        
        pdf.setFontSize(4.5);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 116, 139);
        if (supervisedByData.projectRole) {
          pdf.text(supervisedByData.projectRole, boxX + 2, boxY + 6);
        }
        pdf.text(supervisedByData.email, boxX + 2, boxY + 9);
        pdf.text(`${formattedDate} ${formattedTime}`, boxX + 2, boxY + 11.5);
      }

      let signedPdfBytes: Uint8Array;

      // Paso 2: Si el firmante "Prepared By" tiene certificado digital Y no se seleccionó firma visual únicamente, agregar firma digital
      const preparedByCert = preparedByData?.digitalSignature;
      const hasPreparedByCert = preparedByCert?.status === 'active' && preparedByCert?.certificateUrl;
      
      if (!useVisualSignatureOnly && hasPreparedByCert && preparedByCertPassword) {
        console.log('[SignAndUpload] Firmando con certificado digital del Prepared By...');
        console.log('[SignAndUpload] Certificate:', preparedByCert.certificateName);
        
        try {
          // Obtener el PDF como ArrayBuffer
          const pdfArrayBuffer = pdf.output('arraybuffer');
          
          // Cargar el certificado P12 desde Firebase Storage
          console.log('[SignAndUpload] Descargando certificado...');
          const certResponse = await fetch(preparedByCert.certificateUrl);
          const certArrayBuffer = await certResponse.arrayBuffer();
          const p12Buffer = new Uint8Array(certArrayBuffer);
          
          console.log('[SignAndUpload] Certificado P12 descargado, tamaño:', p12Buffer.length);
          
          // Cargar el PDF con pdf-lib para agregar metadata
          const pdfDoc = await PDFDocument.load(pdfArrayBuffer);
          
          // Agregar metadata del certificado
          const now = new Date();
          pdfDoc.setTitle(`Scanning Report - ${task.id || 'draft'} [DIGITALLY SIGNED]`);
          pdfDoc.setSubject('Digitally Signed Scanning Report');
          pdfDoc.setAuthor(preparedByData.name);
          pdfDoc.setCreator('Brix Platform - Digital Signature Service');
          pdfDoc.setProducer(`Brix Platform | Certificate SN: ${preparedByCert.serialNumber}`);
          pdfDoc.setCreationDate(now);
          pdfDoc.setModificationDate(now);
          
          // Agregar marca visual de certificado en la primera página
          const firstPage = pdfDoc.getPages()[0];
          
          firstPage.drawText('DIGITALLY SIGNED', {
            x: margin * 2.83465,
            y: 20,
            size: 8,
            color: rgb(0, 0.5, 0)
          });
          
          firstPage.drawText(`Certificate SN: ${preparedByCert.serialNumber?.substring(0, 30)}`, {
            x: margin * 2.83465,
            y: 12,
            size: 6,
            color: rgb(0.3, 0.3, 0.3)
          });
          
          firstPage.drawText(`Signer: ${preparedByData.name}`, {
            x: margin * 2.83465,
            y: 6,
            size: 6,
            color: rgb(0.3, 0.3, 0.3)
          });
          
          // Guardar PDF con opciones específicas para compatibilidad con @signpdf
          // useObjectStreams: false es crítico para que @signpdf pueda parsear el PDF
          console.log('[SignAndUpload] Guardando PDF con formato compatible...');
          let pdfToSign = await pdfDoc.save({
            useObjectStreams: false,
            addDefaultPage: false,
          });
          
          console.log('[SignAndUpload] Agregando placeholder para firma digital...');
          
          // Convertir Uint8Array a Buffer para plainAddPlaceholder
          const pdfBuffer = Buffer.from(pdfToSign);
          
          // Agregar placeholder para la firma digital
          const pdfWithPlaceholder = plainAddPlaceholder({
            pdfBuffer: pdfBuffer,
            reason: 'Digital Signature - Brix Platform',
            contactInfo: preparedByData.name,
            name: preparedByData.name,
            location: 'Brix Platform',
          });
          
          pdfToSign = pdfWithPlaceholder;
          
          console.log('[SignAndUpload] Firmando PDF con certificado P12...');
          
          // Crear el signer con el certificado P12
          const signer = new P12Signer(p12Buffer, {
            passphrase: preparedByCertPassword,
          });
          
          // Firmar el PDF
          const signPdfInstance = new SignPdf();
          const signedPdf = await signPdfInstance.sign(pdfToSign, signer);
          
          signedPdfBytes = new Uint8Array(signedPdf);
          
          console.log('[SignAndUpload] ✓ PDF firmado digitalmente correctamente');
          console.log('[SignAndUpload] PDF firmado es NO EDITABLE y contiene firma digital válida');
          
        } catch (certError) {
          console.error('[SignAndUpload] Error al procesar certificado:', certError);
          const errorMessage = certError instanceof Error ? certError.message : 'Unknown error';
          
          // Mostrar modal de confirmación
          const shouldContinue = await new Promise<boolean>((resolve) => {
            const modalOverlay = document.createElement('div');
            modalOverlay.style.position = 'fixed';
            modalOverlay.style.inset = '0';
            modalOverlay.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
            modalOverlay.style.zIndex = '9999';
            modalOverlay.style.display = 'flex';
            modalOverlay.style.alignItems = 'center';
            modalOverlay.style.justifyContent = 'center';

            const modal = document.createElement('div');
            modal.style.backgroundColor = 'white';
            modal.style.borderRadius = '12px';
            modal.style.padding = '24px';
            modal.style.maxWidth = '500px';
            modal.style.boxShadow = '0 20px 25px -5px rgb(0 0 0 / 0.1)';

            modal.innerHTML = `
              <div style="display: flex; flex-direction: column; gap: 16px;">
                <div style="display: flex; gap: 12px; align-items: flex-start;">
                  <svg style="width: 24px; height: 24px; color: #ef4444; flex-shrink: 0;" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                  </svg>
                  <div>
                    <h3 style="margin: 0 0 8px 0; font-size: 18px; font-weight: 600; color: #1f2937;">Error with digital certificate</h3>
                    <p style="margin: 0; font-size: 14px; color: #6b7280; line-height: 1.5;">${errorMessage}</p>
                    <p style="margin: 12px 0 0 0; font-size: 14px; color: #374151; font-weight: 500;">Do you want to save the document with visual signature only?</p>
                  </div>
                </div>
                <div style="display: flex; gap: 12px; justify-content: flex-end;">
                  <button id="cancelBtn" style="px: 16px; padding: 8px 16px; background-color: #f3f4f6; color: #374151; border: none; border-radius: 6px; font-weight: 500; cursor: pointer; font-size: 14px;">No</button>
                  <button id="confirmBtn" style="padding: 8px 16px; background-color: #ef4444; color: white; border: none; border-radius: 6px; font-weight: 500; cursor: pointer; font-size: 14px;">Yes, save with visual signature only</button>
                </div>
              </div>
            `;

            modalOverlay.appendChild(modal);
            document.body.appendChild(modalOverlay);

            const confirmBtn = modal.querySelector('#confirmBtn') as HTMLButtonElement;
            const cancelBtn = modal.querySelector('#cancelBtn') as HTMLButtonElement;

            confirmBtn.addEventListener('click', () => {
              document.body.removeChild(modalOverlay);
              resolve(true);
            });

            cancelBtn.addEventListener('click', () => {
              document.body.removeChild(modalOverlay);
              resolve(false);
            });
          });

          if (!shouldContinue) {
            setIsSigning(false);
            return;
          }
          
          // Si el usuario acepta, usar solo firma visual
          signedPdfBytes = new Uint8Array(pdf.output('arraybuffer'));
        }
        
      } else {
        // Sin certificado digital, solo firma visual
        signedPdfBytes = new Uint8Array(pdf.output('arraybuffer'));
        console.log('[SignAndUpload] PDF con firma visual');
      }

      // Paso 3: Subir a Firebase Storage
      console.log('[SignAndUpload] Subiendo PDF a Firebase Storage...');
      const timestamp = Date.now();
      const taskIdForPath = task.id || `draft-${timestamp}`;
      const filename = `scanning-report-signed-${timestamp}.pdf`;
      const storagePath = `scanning-reports/${userData?.businessId || 'general'}/${taskIdForPath}/${filename}`;
      const storageRef = ref(storage, storagePath);
      
      console.log('[SignAndUpload] Storage path:', storagePath);
      
      const blob = new Blob([signedPdfBytes], { type: 'application/pdf' });
      await uploadBytes(storageRef, blob);
      
      // Obtener URL de descarga
      const downloadUrl = await getDownloadURL(storageRef);
      setSignedPdfUrl(downloadUrl);
      
      console.log('[SignAndUpload] PDF subido exitosamente:', storagePath);
      console.log('[SignAndUpload] Download URL:', downloadUrl);
      
      // Guardar información del PDF firmado en el task
      const signatures = [];
      
      // Agregar firma de "Prepared By"
      signatures.push({
        role: 'preparedBy',
        signerUid: preparedBy,
        signerName: preparedByData.name,
        signerEmail: preparedByData.email,
        signerProjectRole: preparedByData.projectRole || '',
        signedAt: new Date().toISOString(),
        hasCertificate: !useVisualSignatureOnly && hasPreparedByCert && !!preparedByCertPassword
      });
      
      // Agregar firma de "Supervised By" si existe
      if (supervisedBy && supervisedByData) {
        const hasSupervisedByCert = supervisedByData?.digitalSignature?.status === 'active' && supervisedByData?.digitalSignature?.certificateUrl;
        signatures.push({
          role: 'supervisedBy',
          signerUid: supervisedBy,
          signerName: supervisedByData.name,
          signerEmail: supervisedByData.email,
          signerProjectRole: supervisedByData.projectRole || '',
          signedAt: new Date().toISOString(),
          hasCertificate: !useVisualSignatureOnly && hasSupervisedByCert && !!supervisedByCertPassword
        });
      }
      
      const pdfData = {
        url: downloadUrl,
        storagePath: storagePath,
        signedAt: new Date().toISOString(),
        signedBy: {
          uid: preparedBy,
          name: preparedByData.name,
          email: preparedByData.email,
          projectRole: preparedByData.projectRole || ''
        },
        hasCertificate: !useVisualSignatureOnly && hasPreparedByCert && !!preparedByCertPassword,
        signatures: signatures
      };
      
      setSignedPdfData(pdfData);
      
      // Actualizar el task en Firestore
      console.log('[SignAndUpload] Verificando datos del task...');
      console.log('[SignAndUpload] task.id:', task.id);
      console.log('[SignAndUpload] userData?.businessId:', userData?.businessId);
      console.log('[SignAndUpload] task.projectId:', task.projectId);
      console.log('[SignAndUpload] task completo:', task);
      
      const businessId = task.businessId || userData?.businessId;
      if (task.id && businessId && task.projectId) {
        const taskPath = `projects/${businessId}/projects/${task.projectId}/tasks/${task.id}`;
        console.log('[SignAndUpload] ✓ Todos los IDs están presentes');
        console.log('[SignAndUpload] Task path:', taskPath);
        
        const taskRef = doc(db, 'projects', businessId, 'projects', task.projectId, 'tasks', task.id);
        
        // Crear objeto de archivo para el tab Files
        const fileData = {
          name: filename,
          url: downloadUrl,
          uploadDate: new Date().toISOString(),
          type: 'application/pdf',
          size: signedPdfBytes.length,
          uploadedBy: {
            uid: preparedBy,
            name: preparedByData.name,
            email: preparedByData.email
          },
          category: 'Scanning Report',
          isSigned: true
        };
        
        console.log('[SignAndUpload] Updating task with data:', {
          signedPdf: pdfData,
          signed: true,
          fileData: fileData
        });
        
        try {
          await updateDoc(taskRef, {
            'scanningReport.signedPdf': pdfData,
            'scanningReport.signed': true,
            files: arrayUnion(fileData)
          });
          console.log('[SignAndUpload] ✅ Task actualizado exitosamente en Firestore');
          console.log('[SignAndUpload] File data added to files array:', fileData);
        } catch (firestoreError: any) {
          console.error('[SignAndUpload] ❌ Error al actualizar Firestore:', firestoreError);
          console.error('[SignAndUpload] Error details:', {
            message: firestoreError?.message,
            code: firestoreError?.code,
            stack: firestoreError?.stack
          });
          throw firestoreError;
        }
        
        // Notificar al componente padre que se firmó el PDF
        if (onPdfSigned) {
          onPdfSigned();
        }
      }
      
      // Limpiar firma después de firmar
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      setSignature('');
      setCertificatePassword('');
      
      // Toast de éxito
      const toast = document.createElement('div');
      toast.style.position = 'fixed';
      toast.style.top = '20px';
      toast.style.right = '20px';
      toast.style.backgroundColor = '#10b981';
      toast.style.color = 'white';
      toast.style.padding = '16px 24px';
      toast.style.borderRadius = '8px';
      toast.style.boxShadow = '0 10px 15px -3px rgb(0 0 0 / 0.1)';
      toast.style.zIndex = '9999';
      toast.style.display = 'flex';
      toast.style.alignItems = 'center';
      toast.style.gap = '12px';
      toast.style.fontWeight = '500';
      toast.style.fontSize = '14px';
      toast.innerHTML = `
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
        </svg>
        <span>¡Documento firmado exitosamente! Ya puedes descargarlo.</span>
      `;
      document.body.appendChild(toast);
      setTimeout(() => {
        toast.style.transition = 'opacity 0.3s';
        toast.style.opacity = '0';
        setTimeout(() => document.body.removeChild(toast), 300);
      }, 4000);
      
    } catch (error) {
      console.error('[SignAndUpload] Error al firmar y subir PDF:', error);
      
      // Toast de error
      const toast = document.createElement('div');
      toast.style.position = 'fixed';
      toast.style.top = '20px';
      toast.style.right = '20px';
      toast.style.backgroundColor = '#ef4444';
      toast.style.color = 'white';
      toast.style.padding = '16px 24px';
      toast.style.borderRadius = '8px';
      toast.style.boxShadow = '0 10px 15px -3px rgb(0 0 0 / 0.1)';
      toast.style.zIndex = '9999';
      toast.style.display = 'flex';
      toast.style.alignItems = 'center';
      toast.style.gap = '12px';
      toast.style.fontWeight = '500';
      toast.style.fontSize = '14px';
      toast.innerHTML = `
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
        </svg>
        <span>Error al firmar el documento. Intenta nuevamente.</span>
      `;
      document.body.appendChild(toast);
      setTimeout(() => {
        toast.style.transition = 'opacity 0.3s';
        toast.style.opacity = '0';
        setTimeout(() => document.body.removeChild(toast), 300);
      }, 4000);
    } finally {
      setIsSigning(false);
    }
  };

  const generatePreviewFromSignedPdf = async (pdfUrl: string) => {
    setIsGenerating(true);
    try {
      // Cargar el PDF firmado
      const response = await fetch(pdfUrl);
      const pdfBlob = await response.blob();
      const pdfArrayBuffer = await pdfBlob.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfArrayBuffer);
      
      const numPages = pdfDoc.getPageCount();
      const pdfPages: string[] = [];
      
      // Convertir cada página a imagen
      for (let i = 0; i < numPages; i++) {
        const singlePageDoc = await PDFDocument.create();
        const [copiedPage] = await singlePageDoc.copyPages(pdfDoc, [i]);
        singlePageDoc.addPage(copiedPage);
        
        const pdfBytes = await singlePageDoc.save();
        const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });
        const pdfUrl = URL.createObjectURL(pdfBlob);
        
        // Crear iframe temporal para renderizar
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.left = '-9999px';
        iframe.src = pdfUrl;
        document.body.appendChild(iframe);
        
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Capturar como canvas
        const canvas = document.createElement('canvas');
        canvas.width = 794 * 2;
        canvas.height = 1123 * 2;
        const ctx = canvas.getContext('2d');
        
        if (ctx && iframe.contentWindow) {
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
        
        pdfPages.push(canvas.toDataURL('image/png'));
        document.body.removeChild(iframe);
        URL.revokeObjectURL(pdfUrl);
      }
      
      setPages(pdfPages);
      setCurrentPage(0);
    } catch (error) {
      console.error('Error generating preview from signed PDF:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDeleteSignedPdf = async () => {
    if (!signedPdfData || !signedPdfData.storagePath) return;
    
    const confirmed = window.confirm('¿Estás seguro de que deseas eliminar el PDF firmado? Esto permitirá generar y firmar uno nuevo.');
    if (!confirmed) return;
    
    setIsDeleting(true);
    try {
      // Eliminar archivo de Storage
      const storageRef = ref(storage, signedPdfData.storagePath);
      await deleteObject(storageRef);
      
      // Actualizar el task en Firestore para remover el PDF firmado y el archivo del tab Files
      if (task.id && userData?.businessId) {
        const taskRef = doc(db, 'projects', userData.businessId, 'projects', task.projectId, 'tasks', task.id);
        
        // Obtener el task actual para filtrar el archivo
        const taskSnap = await getDoc(taskRef);
        if (taskSnap.exists()) {
          const taskData = taskSnap.data();
          const files = taskData.files || [];
          // Filtrar el archivo del PDF firmado
          const updatedFiles = files.filter((file: any) => file.url !== signedPdfData.url);
          
          await updateDoc(taskRef, {
            'scanningReport.signedPdf': null,
            'scanningReport.signed': false,
            files: updatedFiles
          });
        }
      }
      
      // Limpiar estados
      setSignedPdfUrl('');
      setSignedPdfData(null);
      setSignature('');
      
      // Regenerar preview
      const fetchBusinessLogoAndGenerate = async () => {
        let logoUrl = '';
        // Usar task.businessId primero, luego userData.businessId como fallback
        const businessId = task?.businessId || userData?.businessId;
        if (businessId) {
          try {
            const businessDoc = await getDoc(doc(db, 'businesses', businessId));
            if (businessDoc.exists()) {
              const businessData = businessDoc.data();
              logoUrl = businessData?.logo || '';
              console.log('[ScanningReportModal] Business logo URL:', logoUrl);
            }
          } catch (error) {
            console.error('Error fetching business logo:', error);
          }
        }
        await generatePreview(logoUrl);
      };
      
      await fetchBusinessLogoAndGenerate();
      
      // Notificar al componente padre que se eliminó el PDF
      if (onPdfSigned) {
        onPdfSigned();
      }
      
      // Toast de éxito
      const toast = document.createElement('div');
      toast.style.position = 'fixed';
      toast.style.top = '20px';
      toast.style.right = '20px';
      toast.style.backgroundColor = '#10b981';
      toast.style.color = 'white';
      toast.style.padding = '16px 24px';
      toast.style.borderRadius = '8px';
      toast.style.boxShadow = '0 10px 15px -3px rgb(0 0 0 / 0.1)';
      toast.style.zIndex = '9999';
      toast.innerHTML = 'PDF firmado eliminado exitosamente';
      document.body.appendChild(toast);
      setTimeout(() => {
        toast.style.transition = 'opacity 0.3s';
        toast.style.opacity = '0';
        setTimeout(() => document.body.removeChild(toast), 300);
      }, 3000);
      
    } catch (error) {
      console.error('Error deleting signed PDF:', error);
      alert('Error al eliminar el PDF firmado');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownloadSigned = () => {
    if (!signedPdfUrl) return;
    
    console.log('[Download] Descargando PDF firmado...');
    const link = document.createElement('a');
    link.href = signedPdfUrl;
    link.download = `scanning-report-signed-${task.id || Date.now()}.pdf`;
    link.target = '_blank';
    link.click();
    
    console.log('[Download] Descarga iniciada');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full h-full max-w-[1400px] max-h-[90vh] bg-transparent flex flex-col z-50">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col h-full">
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-md bg-red-50 flex items-center justify-center border border-red-100">
                <svg width="36" height="36" viewBox="0 0 800 263" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 0h800v263H0z" fill="none"/></svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Scanning Report</h3>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {!signedPdfUrl && (
                <button
                  onClick={handleDownload}
                  disabled={!pages.length || isGenerating}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg shadow disabled:opacity-50"
                >
                  {isGenerating ? <FaSpinner className="animate-spin" /> : <FaDownload />} <span className="hidden sm:inline">Download Draft</span>
                </button>
              )}
              {signedPdfUrl && (
                <>
                  <button
                    onClick={handleDownloadSigned}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg shadow"
                  >
                    <FaDownload /> <span className="hidden sm:inline">Download Signed PDF</span>
                  </button>
                  <button
                    onClick={handleDeleteSignedPdf}
                    disabled={isDeleting}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg shadow disabled:opacity-50"
                  >
                    {isDeleting ? <FaSpinner className="animate-spin" /> : <FaTrash />} <span className="hidden sm:inline">Delete Signed PDF</span>
                  </button>
                </>
              )}
              <button onClick={onClose} className="w-10 h-10 rounded-md flex items-center justify-center text-gray-600 hover:bg-gray-100">
                <FaTimes />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-hidden bg-gray-50 grid grid-cols-[1fr_350px]">
            {/* PDF Preview Section */}
            <div className="overflow-auto p-6">
              {isGenerating ? (
                <div className="flex items-center justify-center h-full">
                  <FaSpinner className="animate-spin text-red-600 text-5xl" />
                </div>
              ) : signedPdfUrl ? (
                // Mostrar el PDF firmado en un iframe
                <div className="flex flex-col h-full">
                  <div className="flex-1 bg-white rounded-lg shadow-sm overflow-hidden border">
                    <iframe
                      src={signedPdfUrl}
                      className="w-full h-full"
                      title="Signed PDF Viewer"
                      style={{ minHeight: '70vh' }}
                    />
                  </div>
                  <div className="mt-4 flex items-center justify-center">
                    <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 px-4 py-2 rounded-lg border border-green-200">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium">Viewing signed PDF document</span>
                    </div>
                  </div>
                </div>
              ) : pages.length > 0 ? (
                // Mostrar preview del draft
                <div className="flex flex-col gap-6">
                  <div className="flex-1">
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden border">
                      <img src={pages[currentPage]} alt={`page-${currentPage}`} className="w-full h-auto max-h-[72vh] object-contain bg-gray-100" />
                    </div>

                    <div className="mt-4 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <button onClick={() => setCurrentPage(p => Math.max(0, p - 1))} disabled={currentPage === 0} className="p-2 bg-white border rounded-md shadow-sm disabled:opacity-50">
                          <FaChevronLeft />
                        </button>
                        <div className="text-sm text-gray-600">Page <span className="font-medium text-gray-800">{currentPage + 1}</span> of {pages.length}</div>
                        <button onClick={() => setCurrentPage(p => Math.min(pages.length - 1, p + 1))} disabled={currentPage === pages.length - 1} className="p-2 bg-white border rounded-md shadow-sm disabled:opacity-50">
                          <FaChevronRight />
                        </button>
                      </div>

                      <div className="text-sm text-gray-500">Preview generated from task data</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center text-gray-600 py-12">No preview available</div>
              )}
            </div>

            {/* Signature Panel */}
            <div className="border-l border-gray-200 bg-white p-6 overflow-auto">
              {/* Signers Section */}
              {!signedPdfUrl && (
                <div className="mb-6 pb-6 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Signers</h3>
                  
                  {/* Prepared By */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Prepared By *</label>
                    <select
                      value={preparedBy}
                      onChange={(e) => setPreparedBy(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                    >
                      <option value="">Select signer...</option>
                      {availableSigners.map((signer) => (
                        <option key={signer.uid} value={signer.uid}>
                          {signer.name} {signer.isSelf ? '(You)' : ''}
                        </option>
                      ))}
                    </select>
                    
                    {preparedBy && preparedByData && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg space-y-3">
                        {preparedBySignature ? (
                          <div className="border border-gray-300 rounded-lg overflow-hidden bg-white p-2">
                            <img src={preparedBySignature} alt="Prepared By Signature" className="w-full h-20 object-contain" />
                          </div>
                        ) : (
                          <div className="text-xs text-gray-500 italic">No signature available</div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Supervised By */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Supervised and Revised By</label>
                    <select
                      value={supervisedBy}
                      onChange={(e) => setSupervisedBy(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                    >
                      <option value="">None (optional)</option>
                      {availableSigners.map((signer) => (
                        <option key={signer.uid} value={signer.uid}>
                          {signer.name} {signer.isSelf ? '(You)' : ''}
                        </option>
                      ))}
                    </select>
                    
                    {supervisedBy && supervisedByData && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg space-y-3">
                        {supervisedBySignature ? (
                          <div className="border border-gray-300 rounded-lg overflow-hidden bg-white p-2">
                            <img src={supervisedBySignature} alt="Supervised By Signature" className="w-full h-20 object-contain" />
                          </div>
                        ) : (
                          <div className="text-xs text-gray-500 italic">No signature available</div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Canvas ocultos para cargar firmas */}
                  <canvas ref={preparedByCanvasRef} width="400" height="150" className="hidden" />
                  <canvas ref={supervisedByCanvasRef} width="400" height="150" className="hidden" />
                </div>
              )}
              
              {/* Botones de firma - solo mostrar si NO hay PDF firmado */}
              {!signedPdfUrl && (() => {
                // Definir variables fuera del IIFE para que estén disponibles en todo el scope
                const hasPreparedByCert = preparedByData?.digitalSignature?.status === 'active' && preparedByData?.digitalSignature?.certificateUrl;
                const hasSupervisedByCert = supervisedByData?.digitalSignature?.status === 'active' && supervisedByData?.digitalSignature?.certificateUrl;
                const anyCertificate = hasPreparedByCert || hasSupervisedByCert;
                
                return (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-gray-800">Sign Document</h3>
                  
                  {/* Verificar si alguno de los firmantes tiene certificado digital */}
                  {(() => {
                    
                    if (anyCertificate) {
                      return (
                        <div className="space-y-3">
                          <div className="p-2 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-center gap-1.5 text-green-700 text-xs mb-1">
                              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              <div>
                                <span className="font-medium">Digital certificates available:</span>
                                <div className="text-[10px] mt-1 space-y-0.5">
                                  {hasPreparedByCert && <div>• Prepared By: {preparedByData?.name}</div>}
                                  {hasSupervisedByCert && <div>• Supervised By: {supervisedByData?.name}</div>}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Checkbox para usar solo firma visual */}
                          <div className="flex items-start gap-2 p-2 bg-gray-50 rounded-lg">
                            <input
                              type="checkbox"
                              id="visualSignatureOnly"
                              checked={useVisualSignatureOnly}
                              onChange={(e) => setUseVisualSignatureOnly(e.target.checked)}
                              className="mt-0.5 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                            />
                            <label htmlFor="visualSignatureOnly" className="text-xs text-gray-700 cursor-pointer">
                              <span className="font-medium">Use visual signature only</span>
                              <span className="block text-[10px] text-gray-500 mt-0.5">
                                Sign without digital certificates (No electronic certificate)
                              </span>
                            </label>
                          </div>

                          {/* Campos de contraseña solo si NO se usa firma visual únicamente */}
                          {!useVisualSignatureOnly && (
                            <>
                              {hasPreparedByCert && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Certificate password - {preparedByData?.name}
                                  </label>
                                  <input
                                    type="password"
                                    value={preparedByCertPassword}
                                    onChange={(e) => setPreparedByCertPassword(e.target.value)}
                                    placeholder="Enter certificate password"
                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                  />
                                </div>
                              )}
                              
                              {hasSupervisedByCert && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Certificate password - {supervisedByData?.name}
                                  </label>
                                  <input
                                    type="password"
                                    value={supervisedByCertPassword}
                                    onChange={(e) => setSupervisedByCertPassword(e.target.value)}
                                    placeholder="Enter certificate password"
                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                  />
                                </div>
                              )}
                            </>
                          )}

                          <button
                            onClick={handleSignAndUpload}
                            disabled={
                              !preparedBy || 
                              !preparedBySignature || 
                              (!useVisualSignatureOnly && hasPreparedByCert && !preparedByCertPassword) ||
                              (!useVisualSignatureOnly && hasSupervisedByCert && !supervisedByCertPassword) ||
                              isSigning
                            }
                            className="w-full mt-2 px-3 py-2 text-sm bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                          >
                            {isSigning ? <FaSpinner className="animate-spin text-xs" /> : <FaPen className="text-xs" />}
                            {isSigning ? 'Signing...' : useVisualSignatureOnly ? 'Sign & Upload (Visual Only)' : 'Sign & Upload (with Digital Certificate)'}
                          </button>
                        </div>
                      );
                    } else {
                      return (
                        <div className="space-y-3">
                          <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex items-start gap-1.5 text-blue-700 text-xs">
                              <svg className="w-3.5 h-3.5 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                              </svg>
                              <div>
                                <p className="font-medium">Visual signature only</p>
                                <p className="text-[10px] mt-0.5">No digital certificates available. The document will be signed with visual signatures only.</p>
                              </div>
                            </div>
                          </div>

                          <button
                            onClick={handleSignAndUpload}
                            disabled={!preparedBy || !preparedBySignature || isSigning}
                            className="w-full mt-2 px-3 py-2 text-sm bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                          >
                            {isSigning ? <FaSpinner className="animate-spin text-xs" /> : <FaPen className="text-xs" />}
                            {isSigning ? 'Signing...' : 'Sign & Upload (Visual Only)'}
                          </button>
                        </div>
                      );
                    }
                  })()}
                </div>
                );
              })()}
              
              {signedPdfUrl && (
                <>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Document Status</h3>
                  
                  {/* Mostrar información del documento firmado */}
                  <div className="space-y-4">
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2 text-green-700 mb-2">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="font-medium">Document Signed</span>
                      </div>
                      {signedPdfData && (
                        <div className="text-xs text-gray-700 space-y-1">
                          <div><span className="font-medium">Signed by:</span> {signedPdfData.signedBy?.name}</div>
                          <div><span className="font-medium">Date:</span> {new Date(signedPdfData.signedAt).toLocaleString()}</div>
                          <div><span className="font-medium">Digital Certificate:</span> {signedPdfData.hasCertificate ? 'Yes' : 'Visual only'}</div>
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <button
                        onClick={handleDownloadSigned}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                      >
                        <FaDownload />
                        Download Signed PDF
                      </button>

                      <button
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                      >
                        <FaUserPlus />
                        Request Additional Signature
                      </button>

                      <button
                        onClick={handleDeleteSignedPdf}
                        disabled={isDeleting}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                      >
                        {isDeleting ? <FaSpinner className="animate-spin" /> : <FaTrash />}
                        Delete Signed PDF
                      </button>
                    </div>

                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-xs text-yellow-800">
                        <span className="font-medium">Note:</span> The scanning report data is now locked and cannot be edited. Delete the signed PDF to make changes.
                      </p>
                    </div>
                  </div>
                </>
              )}
              
              {/* Sección antigua de firma - ahora oculta, solo mantener canvas */}
              <div className="hidden">
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Digital Signature</label>
                  
                  {signature ? (
                    <div className="space-y-2">
                      <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white p-2">
                        <img src={signature} alt="Signature" className="w-full h-28 object-contain" />
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={handleOpenSignatureModal}
                          className="flex-1 px-3 py-2 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                        >
                          Redraw Signature
                        </button>
                        <button
                          onClick={() => {
                            const canvas = canvasRef.current;
                            if (!canvas) return;
                            const ctx = canvas.getContext('2d');
                            if (!ctx) return;
                            ctx.clearRect(0, 0, canvas.width, canvas.height);
                            setSignature('');
                          }}
                          className="px-3 py-2 text-xs text-red-600 hover:text-red-800 border border-red-300 hover:border-red-400 rounded-lg font-medium transition-colors"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={handleOpenSignatureModal}
                      className="w-full px-4 py-8 border-2 border-dashed border-gray-300 hover:border-red-500 rounded-lg text-gray-500 hover:text-red-600 transition-colors flex flex-col items-center gap-2"
                    >
                      <FaPen className="text-2xl" />
                      <span className="text-sm font-medium">Draw My Signature</span>
                    </button>
                  )}
                  
                  {/* Canvas oculto para mantener compatibilidad */}
                  <canvas ref={canvasRef} width={225} height={112} className="hidden" />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Signer name</label>
                  <input
                    type="text"
                    value={signerName}
                    readOnly
                    className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-700"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={signDate}
                    readOnly
                    className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-700"
                  />
                </div>

                {hasCertificate ? (
                  <>
                    <div className="p-2 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-1.5 text-green-700 text-xs mb-1">
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="font-medium">Digital certificate loaded</span>
                      </div>
                      {certificateData && (
                        <div className="mt-1 text-[10px] text-gray-700 space-y-0.5 border-t border-green-100 pt-1">
                          <div><span className="font-medium">Issuer:</span> {certificateData.issuerName}</div>
                          {certificateData.organization && (
                            <div><span className="font-medium">Organization:</span> {certificateData.organization}</div>
                          )}
                          <div><span className="font-medium">Country:</span> {certificateData.country}</div>
                          <div><span className="font-medium">Expires:</span> {new Date(certificateData.expiryDate).toLocaleDateString()}</div>
                          <div className="truncate" title={certificateData.certificateName}>
                            <span className="font-medium">File:</span> {certificateData.certificateName}
                          </div>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Certificate password</label>
                      <input
                        type="password"
                        value={certificatePassword}
                        onChange={(e) => setCertificatePassword(e.target.value)}
                        placeholder="Enter password"
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      />
                    </div>

                    {!signedPdfUrl ? (
                      <button
                        onClick={handleSignAndUpload}
                        disabled={!preparedBy || !preparedBySignature || isSigning}
                        className="w-full mt-2 px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                      >
                        {isSigning ? <FaSpinner className="animate-spin text-xs" /> : <FaPen className="text-xs" />}
                        {isSigning ? 'Signing...' : 'Sign & Upload'}
                      </button>
                    ) : (
                      <button
                        onClick={handleDownloadSigned}
                        className="w-full mt-2 px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                      >
                        <FaDownload className="text-xs" />
                        Download Signed PDF
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <div className="p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-start gap-1.5 text-yellow-700 text-xs">
                        <svg className="w-3.5 h-3.5 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <p className="font-medium">No digital certificate</p>
                          <p className="text-[10px] mt-0.5">Upload a certificate in your profile to sign digitally.</p>
                        </div>
                      </div>
                    </div>

                    {!signedPdfUrl ? (
                      <button
                        onClick={handleSignAndUpload}
                        disabled={!preparedBy || !preparedBySignature || isSigning}
                        className="w-full mt-2 px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                      >
                        {isSigning ? <FaSpinner className="animate-spin text-xs" /> : <FaPen className="text-xs" />}
                        {isSigning ? 'Signing...' : 'Sign & Upload (Visual Only)'}
                      </button>
                    ) : (
                      <button
                        onClick={handleDownloadSigned}
                        className="w-full mt-2 px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                      >
                        <FaDownload className="text-xs" />
                        Download Signed PDF
                      </button>
                    )}
                  </>
                )}
              </div>
              </div>
              {/* Fin de sección oculta */}
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Firma */}
      {showSignatureModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-red-600 to-red-700">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <FaPen />
                Draw Your Signature
              </h3>
              <button
                onClick={() => {
                  setShowSignatureModal(false);
                  setSaveSignature(false);
                }}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                {/* Área de dibujo grande */}
                <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white">
                  <canvas
                    ref={modalCanvasRef}
                    width={800}
                    height={400}
                    className="w-full cursor-crosshair touch-none"
                    style={{ touchAction: 'none' }}
                    onMouseDown={(e) => {
                      setIsDrawing(true);
                      const canvas = modalCanvasRef.current;
                      if (!canvas) return;
                      const ctx = canvas.getContext('2d');
                      if (!ctx) return;
                      const rect = canvas.getBoundingClientRect();
                      const scaleX = canvas.width / rect.width;
                      const scaleY = canvas.height / rect.height;
                      ctx.beginPath();
                      ctx.moveTo((e.clientX - rect.left) * scaleX, (e.clientY - rect.top) * scaleY);
                    }}
                    onMouseMove={(e) => {
                      if (!isDrawing) return;
                      const canvas = modalCanvasRef.current;
                      if (!canvas) return;
                      const ctx = canvas.getContext('2d');
                      if (!ctx) return;
                      const rect = canvas.getBoundingClientRect();
                      const scaleX = canvas.width / rect.width;
                      const scaleY = canvas.height / rect.height;
                      ctx.lineTo((e.clientX - rect.left) * scaleX, (e.clientY - rect.top) * scaleY);
                      ctx.strokeStyle = '#000';
                      ctx.lineWidth = 3;
                      ctx.lineCap = 'round';
                      ctx.lineJoin = 'round';
                      ctx.stroke();
                    }}
                    onMouseUp={() => setIsDrawing(false)}
                    onMouseLeave={() => setIsDrawing(false)}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      setIsDrawing(true);
                      const canvas = modalCanvasRef.current;
                      if (!canvas) return;
                      const ctx = canvas.getContext('2d');
                      if (!ctx) return;
                      const rect = canvas.getBoundingClientRect();
                      const touch = e.touches[0];
                      const scaleX = canvas.width / rect.width;
                      const scaleY = canvas.height / rect.height;
                      ctx.beginPath();
                      ctx.moveTo((touch.clientX - rect.left) * scaleX, (touch.clientY - rect.top) * scaleY);
                    }}
                    onTouchMove={(e) => {
                      e.preventDefault();
                      if (!isDrawing) return;
                      const canvas = modalCanvasRef.current;
                      if (!canvas) return;
                      const ctx = canvas.getContext('2d');
                      if (!ctx) return;
                      const rect = canvas.getBoundingClientRect();
                      const touch = e.touches[0];
                      const scaleX = canvas.width / rect.width;
                      const scaleY = canvas.height / rect.height;
                      ctx.lineTo((touch.clientX - rect.left) * scaleX, (touch.clientY - rect.top) * scaleY);
                      ctx.strokeStyle = '#000';
                      ctx.lineWidth = 3;
                      ctx.lineCap = 'round';
                      ctx.lineJoin = 'round';
                      ctx.stroke();
                    }}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      setIsDrawing(false);
                    }}
                  />
                </div>

                {/* Botón para limpiar */}
                <button
                  onClick={() => {
                    const canvas = modalCanvasRef.current;
                    if (!canvas) return;
                    const ctx = canvas.getContext('2d');
                    if (!ctx) return;
                    ctx.clearRect(0, 0, canvas.width, canvas.height);
                  }}
                  className="w-full px-4 py-2 text-sm text-red-600 hover:text-red-800 border border-red-300 hover:border-red-400 rounded-lg font-medium transition-colors"
                >
                  Clear Signature
                </button>

                {/* Checkbox para guardar firma */}
                <label className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors">
                  <input
                    type="checkbox"
                    checked={saveSignature}
                    onChange={(e) => setSaveSignature(e.target.checked)}
                    className="mt-0.5 w-5 h-5 text-red-600 rounded focus:ring-red-500"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">Save my signature for future use</div>
                    <div className="text-xs text-gray-600 mt-1">Your signature will be securely stored and can be reused in future reports.</div>
                  </div>
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex gap-3">
              <button
                onClick={() => {
                  setShowSignatureModal(false);
                  setSaveSignature(false);
                }}
                className="flex-1 px-4 py-2 text-sm text-gray-700 hover:text-gray-900 border border-gray-300 hover:border-gray-400 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSignatureFromModal}
                disabled={isSavingSignature}
                className="flex-1 px-4 py-2 text-sm bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                {isSavingSignature ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FaPen />
                    Use This Signature
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScanningReportModal;
